import * as React from "react";
import { useCallback, useMemo, useState } from "react";
import styled, { css } from "styled-components";
import debounce from "lodash/debounce";
import { ListGroup, ListGroupItem, Input } from "components/bootstrap";
import Spinner from "components/common/Spinner";
import IconButton from "components/common/IconButton";
import useMitreCategories from "security-app/components/SecurityEvents/Alerts/MitreCategories/useMitreCategories";
import { NoSearchResult } from "components/common";
const Container = styled.div(
  ({ theme }) => css`
    width: fit-content;
    color: ${theme.colors.text.primary};
    padding: 3px 10px;
  `
);
const ScrollableArea = styled.div`
  max-height: 60vh;
  overflow: auto;
`;
const StyledListGroup = styled(ListGroup)(
  ({ $indent }) => css`
    margin-bottom: 0;
    ${$indent && `margin-left: 10px;`}
  `
);
const StyledListGroupItem = styled(ListGroupItem)`
  display: flex;
  padding: 2px;
  align-items: center;
`;
const ExpandIcon = ({
  expanded,
  onChange
}) => {
  const onClick = useCallback(
    (e) => {
      e.stopPropagation();
      onChange((_expanded) => !_expanded);
    },
    [onChange]
  );
  return /* @__PURE__ */ React.createElement(
    IconButton,
    {
      title: `${expanded ? "Collapse" : "Expand"} category`,
      name: expanded ? "keyboard_arrow_down" : "chevron_right",
      onClick
    }
  );
};
const Abbreviate = styled.div`
  max-width: 350px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;
const CategoryButton = styled.button`
  flex: 1;
  min-width: 0;
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  text-align: left;
  font: inherit;
  color: inherit;
`;
const Category = ({
  name,
  suggestion,
  onClick
}) => {
  var _a;
  const [expanded, setExpanded] = useState(true);
  const _onClick = useCallback(() => onClick(suggestion.id), [onClick, suggestion.id]);
  return /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(StyledListGroupItem, { key: `filter-value-${suggestion.id}` }, /* @__PURE__ */ React.createElement(CategoryButton, { type: "button", onClick: _onClick }, /* @__PURE__ */ React.createElement(Abbreviate, { title: suggestion.value }, suggestion.value)), ((_a = suggestion.children) == null ? void 0 : _a.length) > 0 ? /* @__PURE__ */ React.createElement(ExpandIcon, { expanded, onChange: setExpanded }) : null), expanded ? /* @__PURE__ */ React.createElement(StyledListGroup, { $indent: true }, suggestion.children.map((s) => /* @__PURE__ */ React.createElement(Category, { key: `${name}-${s.id}`, name: [name, s.id].join(","), suggestion: s, onClick }))) : null);
};
const SearchInput = styled(Input)`
  margin-bottom: 6px;
`;
const Tactics = ({ onClick, isLoading, suggestions, onFilter }) => {
  const debounceOnSearch = debounce((value) => onFilter(value), 1e3);
  return /* @__PURE__ */ React.createElement(Container, null, /* @__PURE__ */ React.createElement(
    SearchInput,
    {
      autoFocus: true,
      type: "text",
      id: "search-filters-input",
      formGroupClassName: "",
      placeholder: "Search for tactic/technique",
      onChange: ({ target: { value } }) => debounceOnSearch(value)
    }
  ), isLoading && /* @__PURE__ */ React.createElement(Spinner, null), (suggestions == null ? void 0 : suggestions.length) > 0 ? /* @__PURE__ */ React.createElement(ScrollableArea, null, /* @__PURE__ */ React.createElement(StyledListGroup, null, suggestions.map((tactic) => /* @__PURE__ */ React.createElement(Category, { key: tactic.id, name: tactic.id, suggestion: tactic, onClick })))) : /* @__PURE__ */ React.createElement(NoSearchResult, null, "Cannot show Tactics/Techniques because you are missing Illuminate."));
};
const filterSuggestions = (node, lowerQuery, skipCheck = false) => {
  var _a;
  const filteredChildren = (_a = node.children) == null ? void 0 : _a.map((child) => filterSuggestions(child, lowerQuery)).filter((child) => child !== null);
  if (skipCheck || node.value.toLowerCase().includes(lowerQuery) || (filteredChildren == null ? void 0 : filteredChildren.length) > 0) {
    return { ...node, children: filteredChildren };
  }
  return null;
};
export const TacticTechniqueFilter = ({ onClick }) => {
  const { data: categories, isInitialLoading } = useMitreCategories();
  const [filter, setFilter] = useState(void 0);
  const suggestions = useMemo(
    () => {
      var _a;
      return ((_a = categories == null ? void 0 : categories.tactics) != null ? _a : []).map(({ id, name, techniques }) => ({
        id,
        value: `${name} (${id})`,
        children: techniques.map((technique) => ({
          id: technique.id,
          value: `${technique.name} (${technique.id})`,
          children: technique.subtechniques.map((subtechnique) => ({
            id: subtechnique.id,
            value: `${subtechnique.name} (${subtechnique.id})`,
            children: []
          }))
        }))
      }));
    },
    [categories == null ? void 0 : categories.tactics]
  );
  const filteredSuggestions = useMemo(
    () => filter ? filterSuggestions({ id: "root", value: "root", children: suggestions }, filter.toLocaleLowerCase(), true).children : suggestions,
    [filter, suggestions]
  );
  return /* @__PURE__ */ React.createElement(Tactics, { onClick, suggestions: filteredSuggestions, isLoading: isInitialLoading, onFilter: setFilter });
};
const TacticTechniqueFilterContainer = ({ onSubmit }) => {
  const onClick = useCallback(
    (value) => onSubmit(
      {
        value,
        title: value
      },
      true
    ),
    [onSubmit]
  );
  return /* @__PURE__ */ React.createElement(TacticTechniqueFilter, { onClick });
};
export default TacticTechniqueFilterContainer;
